import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Check, MessageSquare, CreditCard, Zap, Building, Crown } from 'lucide-react';
import { createStripeCustomer } from '@/api/stripe';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month: number | null;
  is_active: boolean;
  trial_period_days: number;
  created_at?: string;
  updated_at?: string;
}

const StreamlinedSignup: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company: '',
  });

  const { data: plans } = useQuery({
    queryKey: ["activePlans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .eq("is_active", true)
        .order("price");

      if (error) throw error;
      return data as Plan[];
    },
  });

  // Debug: Log plans data when it loads
  React.useEffect(() => {
    if (plans) {
      console.log('📋 Plans loaded:', plans);
      plans.forEach(plan => {
        console.log(`Plan: ${plan.name}, Price: $${plan.price}, Trial: ${plan.trial_period_days} days`);
      });
    }
  }, [plans]);

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return false;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return false;
    }

    if (!selectedPlanId) {
      toast({
        title: "Plan Selection Required",
        description: "Please select a plan to continue.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            company: formData.company || undefined,
          },
          emailRedirectTo: `${window.location.origin}/portal/auth?verified=true`
        }
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // Update user profile with plan assignment
      const selectedPlan = plans?.find(p => p.id === selectedPlanId);
      const profileUpdateData = {
        name: formData.name,
        company: formData.company || null,
        plan_id: selectedPlanId,
        subscription_status: selectedPlan && selectedPlan.price > 0 ? 'pending' : 'free',
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdateData)
        .eq('id', authData.user.id);

      if (profileError) {
        console.error('Failed to update profile:', profileError);
      }

      // Create Stripe customer
      try {
        await createStripeCustomer(formData.email, formData.name);
      } catch (stripeError) {
        console.error('Failed to create Stripe customer:', stripeError);
        // Continue with signup process even if Stripe customer creation fails
      }

      // Debug: Log plan information
      console.log('Selected plan:', selectedPlan);
      console.log('Plan price:', selectedPlan?.price);
      console.log('Stripe price ID:', selectedPlan?.stripe_price_id);
      console.log('Stripe config available:', !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

      // Handle payment processing
      const hasStripeConfig = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      
      if (selectedPlan && hasStripeConfig && selectedPlan.stripe_price_id) {
        // All plans go through Stripe (even free ones for future upgrades)
        toast({
          title: "Account Created!",
          description: selectedPlan.price > 0 
            ? `Redirecting to secure checkout for ${selectedPlan.name}...`
            : `Setting up your ${selectedPlan.name} plan...`,
        });

        // Initialize Stripe and redirect to checkout
        const { initializeStripe } = await import('@/api/stripe');
        const stripe = await initializeStripe();

        if (stripe) {
          if (selectedPlan.price > 0) {
            // Paid plan - redirect to Stripe checkout
            const { error } = await stripe.redirectToCheckout({
              lineItems: [{
                price: selectedPlan.stripe_price_id,
                quantity: 1,
              }],
              mode: 'subscription',
              successUrl: `${window.location.origin}/portal?success=true&plan=${selectedPlanId}`,
              cancelUrl: `${window.location.origin}/portal/billing?canceled=true`,
              customerEmail: authData.user.email,
            });

            if (error) {
              console.error('Stripe redirect error:', error);
              toast({
                title: "Checkout Error",
                description: `Error: ${error.message}`,
                variant: "destructive",
              });
              navigate('/portal/billing?error=stripe');
            }
            return;
          } else {
            // Free plan - redirect to portal with success message
            toast({
              title: "Account Created!",
              description: "Welcome to IntentDrive.ai! You can upgrade your plan anytime.",
            });
            navigate('/portal?plan=free&success=true');
            return;
          }
        } else {
          throw new Error('Failed to initialize Stripe');
        }
      } else {
        // No Stripe config or no plan selected
        const errorMsg = !hasStripeConfig 
          ? 'Stripe not configured - demo mode'
          : !selectedPlan 
          ? 'No plan selected'
          : 'No Stripe price ID for plan';
        
        console.warn('Stripe checkout not available:', errorMsg);
        toast({
          title: "Account Created!",
          description: `${errorMsg}. Please check your email to verify your account.`,
        });
        navigate('/portal?demo=true');
      }

    } catch (error: any) {
      console.error('Signup error:', error);
      toast({
        title: 'Signup Failed',
        description: error.message || 'Failed to create account',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanIcon = (planName: string) => {
    if (planName.toLowerCase().includes('core')) return <Zap className="h-6 w-6" />;
    if (planName.toLowerCase().includes('growth')) return <Building className="h-6 w-6" />;
    if (planName.toLowerCase().includes('enterprise')) return <Crown className="h-6 w-6" />;
    return <CreditCard className="h-6 w-6" />;
  };

  const getPlanColor = (planName: string) => {
    if (planName.toLowerCase().includes('core')) return 'border-blue-200 bg-blue-50';
    if (planName.toLowerCase().includes('growth')) return 'border-green-200 bg-green-50';
    if (planName.toLowerCase().includes('enterprise')) return 'border-purple-200 bg-purple-50';
    return 'border-gray-200 bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <MessageSquare className="mx-auto h-16 w-16 text-primary mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Start Your IntentDrive.ai Journey
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Create your account and choose your plan in one simple step. Start building intelligent chatbots today.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Account Information */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Account Information
                </CardTitle>
                <CardDescription>
                  Tell us about yourself to get started
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Your Company"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    required
                    minLength={6}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Plan Selection */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Choose Your Plan
                </CardTitle>
                <CardDescription>
                  Select the plan that best fits your needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {plans?.map((plan) => (
                    <div
                      key={plan.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedPlanId === plan.id
                          ? 'border-primary bg-primary/5 shadow-md'
                          : `${getPlanColor(plan.name)} hover:border-primary/50`
                      }`}
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${selectedPlanId === plan.id ? 'bg-primary text-white' : 'bg-white'}`}>
                            {getPlanIcon(plan.name)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{plan.name}</h3>
                            <p className="text-sm text-gray-600">{plan.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {/* Show trial info prominently */}
                          {plan.trial_period_days > 0 && (
                            <div className="mb-2">
                              <p className="text-sm text-green-600 font-bold bg-green-50 px-2 py-1 rounded border border-green-200">
                                🎉 {plan.trial_period_days}-DAY FREE TRIAL
                              </p>
                            </div>
                          )}
                          <p className="text-2xl font-bold">
                            ${plan.price}
                            <span className="text-sm font-normal text-gray-600">/mo</span>
                          </p>
                          {plan.trial_period_days > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Then ${plan.price}/month
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 gap-2">
                        {plan.features.slice(0, 3).map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                        {plan.features.length > 3 && (
                          <p className="text-sm text-gray-500 ml-6">
                            +{plan.features.length - 3} more features
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <Button
              type="submit"
              size="lg"
              className="px-8 py-3 text-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                'Creating Account...'
              ) : selectedPlanId && plans?.find(p => p.id === selectedPlanId)?.price > 0 ? (
                `Create Account & Pay $${plans?.find(p => p.id === selectedPlanId)?.price}/mo`
              ) : (
                'Create Free Account'
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              By creating an account, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </form>

        {/* Login Link */}
        <div className="text-center mt-8">
          <Button
            variant="link"
            onClick={() => navigate('/portal/auth')}
            className="text-gray-600"
          >
            Already have an account? Sign in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StreamlinedSignup;
